import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import { Logo } from "../Logo";
import { LanguageSwitcher } from "../LanguageSwitcher";
import { useLanguage } from "../../i18n/LanguageContext";
import { trackEvent } from "../../lib/analytics";

/**
 * LegalPage — renders a legal document (TOS or Privacy) driven entirely by i18n.
 * Usage: <LegalPage docKey="tos" testId="page-kayttoehdot" />
 */
export const LegalPage = ({ docKey, testId }) => {
  const { t, tRaw, lang } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [docKey]);

  const doc = tRaw(`legal.${docKey}`) || {};
  const sections = Array.isArray(doc.sections) ? doc.sections : [];

  return (
    <div className="relative min-h-screen bg-[#0F0F0F] text-white" data-testid={testId}>
      {/* Top hazard rail */}
      <div className="h-2 hazard-stripes-anim" />

      {/* Header */}
      <header className="border-b-2 border-[#1f1f1f]">
        <div className="max-w-[1000px] mx-auto px-5 md:px-10 py-6 flex items-center justify-between gap-4">
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center gap-3 group"
          >
            <Logo size={40} aura testId="legal-logo" />
            <div className="leading-none">
              <div className="font-display font-bold text-white text-lg tracking-wider">
                DUUNARI
              </div>
              <div className="font-mono text-[9px] tracking-[0.25em] text-[#FFEA00] mt-1">
                v1.0 / {lang.toUpperCase()}
              </div>
            </div>
          </Link>

          <div className="flex items-center gap-3">
            <LanguageSwitcher variant="legal" />
            <Link
              to="/"
              data-testid="legal-back-button"
              className="btn-hazard inline-flex items-center gap-2 px-4 py-2.5 border-2 border-[#FFEA00] text-[#FFEA00] font-display font-bold uppercase text-xs tracking-widest hover:text-black transition-colors"
            >
              <ArrowLeft className="w-4 h-4 stroke-[2.5]" />
              <span>{t("legal.shared.back")}</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Body */}
      <article className="max-w-[1000px] mx-auto px-5 md:px-10 py-14 md:py-20">
        {doc.eyebrow && (
          <div className="font-mono text-[11px] tracking-[0.3em] text-[#FFEA00] uppercase mb-4">
            {doc.eyebrow}
          </div>
        )}
        <h1
          data-testid={`legal-title-${docKey}`}
          className="font-display font-bold text-white text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight leading-[0.95]"
        >
          {doc.title}
        </h1>
        <p className="mt-4 font-mono text-xs tracking-widest text-[#A3A3A3] uppercase">
          {t("legal.shared.last_updated_label")}: {t("legal.shared.last_updated_value")}
        </p>

        {/* Hazard divider */}
        <div className="mt-8 w-32 h-1.5 hazard-stripes-anim" />

        {doc.intro && (
          <p className="mt-10 text-[#A3A3A3] italic legal-prose">{doc.intro}</p>
        )}

        <div className="legal-prose">
          {sections.map((s, i) => (
            <LegalSection key={i} section={s} />
          ))}
        </div>

        {/* Contact box */}
        <div className="mt-16 border-2 border-[#FFEA00]/40 bg-[#0a0a0a] p-6 flex items-center gap-4">
          <div className="w-12 h-12 border-2 border-[#FFEA00] bg-[#FFEA00]/[0.06] flex items-center justify-center shrink-0">
            <Mail className="w-6 h-6 text-[#FFEA00]" />
          </div>
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] text-[#A3A3A3] uppercase">
              {t("legal.shared.questions")}
            </div>
            <a
              href="mailto:duunari.online@gmail.com"
              onClick={() => trackEvent("Click: Email")}
              className="font-display text-white hover:text-[#FFEA00] text-lg uppercase tracking-wide"
            >
              duunari.online@gmail.com
            </a>
          </div>
        </div>

        {/* Bottom back link */}
        <div className="mt-12 flex justify-center">
          <Link
            to="/"
            data-testid="legal-back-bottom"
            className="inline-flex items-center gap-2 text-[#A3A3A3] hover:text-[#FFEA00] font-display uppercase text-sm tracking-widest transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("legal.shared.back_to_home")}
          </Link>
        </div>
      </article>

      {/* Bottom hazard rail */}
      <div className="h-2 hazard-stripes-anim" />
    </div>
  );
};

/** Renders a section based on its type. */
const LegalSection = ({ section }) => {
  return (
    <section className="mt-10">
      <h2 className="font-display font-bold text-white text-xl md:text-2xl uppercase tracking-tight mb-3 border-l-4 border-[#FFEA00] pl-4">
        {section.title}
      </h2>
      <div className="text-[#D4D4D4] text-base md:text-[17px] leading-relaxed space-y-3">
        <SectionBody section={section} />
      </div>
    </section>
  );
};

const SectionBody = ({ section }) => {
  const type = section.type;

  if (type === "html") {
    return (
      <>
        {(section.items || []).map((html, i) => (
          <p key={i} dangerouslySetInnerHTML={{ __html: html }} />
        ))}
      </>
    );
  }

  if (type === "list") {
    return (
      <ul className="list-none space-y-2">
        {(section.items || []).map((item, i) => (
          <li key={i} className="flex items-start gap-2.5">
            <span className="text-[#FFEA00] font-bold mt-0.5">▸</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }

  if (type === "highlight") {
    return (
      <div className="p-5 border-2 border-[#FFEA00] bg-[#FFEA00]/[0.04]">
        <p className="text-white font-display font-bold text-lg md:text-xl uppercase tracking-tight">
          {section.heading}
          {section.accent && (
            <>
              {" "}<span className="text-[#FFEA00]">{section.accent}</span>
            </>
          )}
        </p>
        {section.body && <p className="mt-3 text-[#D4D4D4]">{section.body}</p>}
      </div>
    );
  }

  if (type === "rights") {
    return (
      <ul className="list-none space-y-2">
        {(section.items || []).map(([label, desc], i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="text-[#FFEA00] font-bold mt-0.5">▸</span>
            <div>
              <span className="font-display font-bold text-white uppercase tracking-wide text-sm">
                {label}.
              </span>{" "}
              <span>{desc}</span>
            </div>
          </li>
        ))}
      </ul>
    );
  }

  return null;
};
